import { useEffect, useRef, useState } from 'react';
import { canvasWidth, groundOffsetY, groundSlope, groundYEnd } from './constants';
import HitBox from './HitBox';
import hitBoxesCollide from './hitBoxesCollide';

const gameSpeed = 1.5;
const initialObstacleDelayMs = 2000;
const maxDifficultyMs = 1000 * 30 + initialObstacleDelayMs;
const minMsBetweenObstacles = 1000;

// Spawn rate after the minimum interval (~1% / 10% chance per 60 Hz frame)
const minObstacleSpawnRatePerMs = 0.000603;
const maxObstacleSpawnRatePerMs = 0.00632;
const getSpawnChanceForDelta = (spawnRatePerMs: number, msDelta: number) => 1 - Math.exp(-spawnRatePerMs * msDelta);

const minTreeSpawnChance = 0.3;
const maxTreeSpawnChance = 0.7;

const minYOffset = 0.5 * groundOffsetY;
const maxYOffset = 1.5 * groundOffsetY;

// Based on the coll-trees icon
const treeBase64 =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjE0Ni42NzYxIDI2MS4wMTEgMTAuMzA0MiAyMC41NiIgd2lkdGg9IjEwLjMwNDJweCIgaGVpZ2h0PSIyMC41NnB4Ij48cGF0aCBkPSJNIDE1Ni45NzIgMjY4LjEyMSBDIDE1Ni45NzIgMjY2Ljk1MSAxNTYuMzQyIDI2NS45MzEgMTU1LjQwMiAyNjUuMzgxIEMgMTU1LjAyMiAyNjUuMTYxIDE1NC44MzIgMjY0Ljg3MSAxNTQuODMyIDI2NC4wMjEgQyAxNTQuODMyIDI2Mi4zNjEgMTUzLjQ5MiAyNjEuMDExIDE1MS44MzIgMjYxLjAxMSBDIDE1MC4xNzIgMjYxLjAxMSAxNDguODMyIDI2Mi4zNjEgMTQ4LjgzMiAyNjQuMDIxIEMgMTQ4LjgzMiAyNjQuODcxIDE0OC42NDIgMjY1LjE2MSAxNDguMjYyIDI2NS4zODEgQyAxNDcuMzIyIDI2NS45MzEgMTQ2LjY5MiAyNjYuOTUxIDE0Ni42OTIgMjY4LjEyMSBDIDE0Ni42OTIgMjY4Ljg4MSAxNDYuOTYyIDI2OS41OTEgMTQ3LjQxMiAyNzAuMTMxIEMgMTQ3Ljc4MiAyNzAuNTgxIDE0Ny43NTIgMjcxLjIzMSAxNDcuMzgyIDI3MS42OTEgQyAxNDYuOTAyIDI3Mi4yOTEgMTQ2LjYzMiAyNzMuMDcxIDE0Ni42ODIgMjczLjkwMSBDIDE0Ni43NjIgMjc1LjA0MSAxNDcuNDkyIDI3Ni4wNTEgMTQ4LjUyMiAyNzYuNTQxIEMgMTQ5LjI1MiAyNzYuODkxIDE0OS45MzIgMjc2LjkxMSAxNTAuNTYyIDI3Ni43NjEgTCAxNTAuNTQyIDI3OC45OTEgQyAxNTAuNTQyIDI3OS44NTEgMTUwLjExMiAyODEuMTQxIDE0OS42ODIgMjgxLjU3MSBMIDE1My45NzIgMjgxLjU3MSBDIDE1My41NDIgMjgxLjE0MSAxNTMuMTEyIDI3OS44NTEgMTUzLjExMiAyNzguOTkxIEwgMTUzLjA5MiAyNzYuNzYxIEMgMTUzLjcxMiAyNzYuOTExIDE1NC40MDIgMjc2Ljg4MSAxNTUuMTMyIDI3Ni41NDEgQyAxNTYuMTYyIDI3Ni4wNTEgMTU2Ljg5MiAyNzUuMDQxIDE1Ni45NzIgMjczLjkwMSBDIDE1Ny4wMzIgMjczLjA2MSAxNTYuNzYyIDI3Mi4yOTEgMTU2LjI4MiAyNzEuNjkxIEMgMTU1LjkxMiAyNzEuMjMxIDE1NS44ODIgMjcwLjU4MSAxNTYuMjUyIDI3MC4xMzEgQyAxNTYuNzAyIDI2OS41ODEgMTU2Ljk3MiAyNjguODgxIDE1Ni45NzIgMjY4LjEyMSBaIiBzdHlsZT0ic3Ryb2tlLXdpZHRoOiAxOyIgaWQ9Im9iamVjdC0wIi8+PC9zdmc+';
const treeWidth = 152;
const treeHeight = 304;
const treeHitBoxOffsets = [
  {
    left: Math.floor(treeWidth / 3.5),
    right: Math.floor(treeWidth / 3.5),
    top: Math.floor(treeHeight / 16),
    bottom: 0
  }
];
const treeInitialX = canvasWidth;
const treeInitialY = groundYEnd - treeHeight;

// Based on the coll-scramble icon
const rockBase64 =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjE5MS40NDM1IDIwNi43MzY2IDI0LjAwMDUgMjMuMDc3MyIgd2lkdGg9IjI0LjAwMDVweCIgaGVpZ2h0PSIyMy4wNzczcHgiPjxnIGlkPSJvYmplY3QtMCI+PHBhdGggZD0iTSAyMDEuMjg3IDIxNy44NjggTCAyMDMuOTg1IDIxOS40ODggTCAyMDQuNTQ3IDIyMi4yOTcgTCAyMDQuNTgzIDIyMi40OCBMIDIwNi40MjkgMjI0LjMyNSBMIDIwNy41MjcgMjI5LjgxMyBMIDE5Mi4zNjcgMjI5LjgxMyBDIDE5MS44MDMgMjI5LjgxMyAxOTEuMzcgMjI5LjMxIDE5MS40NTQgMjI4Ljc1MiBMIDE5Mi4zNDQgMjIyLjg5NyBDIDE5Mi4zNzcgMjIyLjY3NSAxOTIuNDkxIDIyMi40NzIgMTkyLjY2MiAyMjIuMzI4IEwgMTk0LjA5MiAyMjAuMzgzIEwgMTk2LjI1NyAyMTUuOTc1IEwgMjAxLjI4NyAyMTcuODY4IFoiIHN0eWxlPSJzdHJva2Utd2lkdGg6IDE7Ii8+PHBhdGggZD0iTSAyMTMuNzE4IDIyMC44ODMgQyAyMTMuNzQ1IDIyMC45MzggMjEzLjc2NCAyMjAuOTk0IDIxMy43NzYgMjIxLjA1MiBMIDIxNS40MjggMjI4Ljc0NCBDIDIxNS41NDkgMjI5LjMwNCAyMTQuOTY5IDIyOS44MTIgMjE0LjIwNiAyMjkuODE0IEwgMjA2LjQ5OCAyMjkuODE0IEwgMjA0LjkxIDIyMy45MDEgTCAyMDQuODYyIDIyMy43MTkgTCAyMDIuMzgyIDIyMS44NzMgTCAyMDEuNjA0IDIxOC45NzggTCAyMDEuNTMzIDIxOC43MTEgTCAxOTkuNDc0IDIxNy43OTIgTCAyMDIuODY1IDIxNC43NjIgTCAyMDkuODY3IDIxMy4zNDcgTCAyMTMuNzE4IDIyMC44ODMgWiIgc3R5bGU9InN0cm9rZS13aWR0aDogMTsiLz48cGF0aCBkPSJNIDIwMi4xNTYgMjA3LjI3NCBDIDIwMi40NTMgMjA2LjgxMSAyMDMuMDY0IDIwNi42MTYgMjAzLjYgMjA2LjgxMyBMIDIwOC41MTEgMjA4LjU5OSBDIDIwOC44ODUgMjA4LjczNiAyMDkuMTU4IDIwOS4wNCAyMDkuMjM5IDIwOS40MDYgTCAyMTAuMzcyIDIxNC41MTggTCAyMDYuMDY4IDIxNC45MTkgTCAyMDUuNzQ4IDIxNC45NSBMIDIwMS45NDEgMjE5LjIxOSBMIDIwMC44NDIgMjE4LjYwNSBMIDE5NS4xMDcgMjE4LjYwNSBMIDE5Ni40OTQgMjEyLjc2NiBDIDE5Ni41NzEgMjEyLjQ0IDE5Ni44IDIxMi4xNjYgMTk3LjEyIDIxMi4wMTggTCAxOTkuOTY1IDIxMC42ODcgTCAyMDIuMTU2IDIwNy4yNzQgWiIgc3R5bGU9InN0cm9rZS13aWR0aDogMTsiLz48L2c+PC9zdmc+';
const rockSize = 136;
const rockHitBoxOffsets = [
  // Bottom portion
  {
    left: Math.floor(rockSize / 12),
    right: Math.floor(rockSize / 12),
    top: Math.floor(rockSize / 1.5),
    bottom: 0
  },
  // Middle portion
  {
    left: Math.floor(rockSize / 4.6),
    right: Math.floor(rockSize / 4),
    top: Math.floor(rockSize / 4.6),
    bottom: 0
  },
  // Top portion
  {
    left: Math.floor(rockSize / 2.4),
    right: Math.floor(rockSize / 3.4),
    top: Math.floor(rockSize / 16),
    bottom: 0
  }
];
const rockInitialX = canvasWidth;
const rockInitialY = groundYEnd - rockSize;

const base64s = [treeBase64, rockBase64];

const useObstaclesRef = (onComponentLoad: () => void, onCollision: () => void, debugMode = false) => {
  const imagesRefs = useRef<HTMLImageElement[]>([]);
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);

  useEffect(() => {
    base64s.forEach(base64 => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        setLoadedImagesCount(prev => prev + 1);
      };
      imagesRefs.current.push(img);
    });
  }, []);

  useEffect(() => {
    if (loadedImagesCount === base64s.length) {
      onComponentLoad();
    }
  }, [loadedImagesCount, onComponentLoad]);

  const obstaclesRef = useRef({
    msCounter: 0,
    treeXYs: [] as [number, number][],
    rockXYs: [] as [number, number][],
    lastObstacleTime: 0,
    difficultyMultiplier: 0,
    reset() {
      this.msCounter = 0;
      this.treeXYs = [];
      this.rockXYs = [];
      this.lastObstacleTime = 0;
      this.difficultyMultiplier = 0;
    },
    getHitBoxes() {
      // Returns the hit boxes for all obstacles in the form of [x, y, width, height]
      const hitBoxes: HitBox[] = this.treeXYs.flatMap(([treeX, treeY]) => {
        return treeHitBoxOffsets.map(offset => [
          treeX + offset.left,
          treeY + offset.top,
          treeWidth - offset.left - offset.right,
          treeHeight - offset.top - offset.bottom
        ]);
      });
      hitBoxes.push(
        ...this.rockXYs.flatMap(([rockX, rockY]) => {
          return rockHitBoxOffsets.map(
            offset =>
              [rockX + offset.left, rockY + offset.top, rockSize - offset.left - offset.right, rockSize - offset.top - offset.bottom] as HitBox
          );
        })
      );
      return hitBoxes;
    },
    detectCollision(hikerHitBoxes: readonly HitBox[]) {
      if (
        hikerHitBoxes.some(hitBox =>
          this.getHitBoxes().some(obstacleHitBox => {
            return hitBoxesCollide(hitBox, obstacleHitBox);
          })
        )
      ) {
        onCollision();
      }
    },
    draw(ctx: CanvasRenderingContext2D, msDelta: number) {
      this.msCounter += msDelta;

      // Delay the first obstacle spawn to let the user read the instructions
      if (this.msCounter < initialObstacleDelayMs) {
        return;
      }

      // Difficulty increases as the game progresses
      this.difficultyMultiplier = Math.min(1, this.msCounter / maxDifficultyMs);

      const obstacleDelta = this.msCounter - this.lastObstacleTime;
      // Increase likelihood of spawning an obstacle as the game speed increases
      if (obstacleDelta > minMsBetweenObstacles / gameSpeed) {
        const spawnRate = minObstacleSpawnRatePerMs + (maxObstacleSpawnRatePerMs - minObstacleSpawnRatePerMs) * this.difficultyMultiplier;

        if (Math.random() < getSpawnChanceForDelta(spawnRate, msDelta)) {
          // More likely to spawn a tree as the difficulty increases
          const treeSpawnChance = minTreeSpawnChance + (maxTreeSpawnChance - minTreeSpawnChance) * this.difficultyMultiplier;
          const yOffset = Math.random() * (maxYOffset - minYOffset) + minYOffset;
          if (Math.random() < treeSpawnChance) {
            this.treeXYs.push([treeInitialX, treeInitialY + yOffset]);
          } else {
            this.rockXYs.push([rockInitialX, rockInitialY + yOffset]);
          }
          this.lastObstacleTime = this.msCounter;
        }
      }

      const xDelta = msDelta * gameSpeed;
      const yDelta = xDelta * groundSlope;

      for (let i = 0; i < this.treeXYs.length; i++) {
        this.treeXYs[i] = [this.treeXYs[i][0] - xDelta, this.treeXYs[i][1] + yDelta];
        ctx.drawImage(imagesRefs.current[0], this.treeXYs[i][0], this.treeXYs[i][1], treeWidth, treeHeight);
      }
      this.treeXYs = this.treeXYs.filter(([treeX]) => treeX + treeWidth > 0);

      for (let i = 0; i < this.rockXYs.length; i++) {
        this.rockXYs[i] = [this.rockXYs[i][0] - xDelta, this.rockXYs[i][1] + yDelta];
        ctx.drawImage(imagesRefs.current[1], this.rockXYs[i][0], this.rockXYs[i][1], rockSize, rockSize);
      }
      this.rockXYs = this.rockXYs.filter(([rockX]) => rockX + rockSize > 0);

      if (debugMode) {
        ctx.save();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'red';
        this.getHitBoxes().forEach(hitBox => {
          ctx.strokeRect(...hitBox);
        });
        ctx.restore();
      }
    }
  });

  return obstaclesRef;
};

export default useObstaclesRef;
